<template>
  <LoadingContainer :is-loading="isLoading">
    <div class="card">
      <div class="card-header">
        <div class="card-header-title">
          <div class="title is-6 is-marginless">
            Stand-alone Card Links
            <span class="tag ml-2">
              {{ items.length }}
            </span>
          </div>
        </div>
        <div class="card-header-icon">
          <router-link
            :to="{ name: 'content.home.standalone-cards.add' }"
            class="button"
          >
            <span class="icon is-small">
              <i class="fa fa-plus" />
            </span>
            <span>Προσθήκη</span>
          </router-link>
        </div>
      </div>

      <div class="card-content">
        <div class="columns">
          <div class="column is-full">
            <div class="table-wrapper">
              <table
                v-if="items.length"
                class="table is-bordered is-striped is-marginless is-fullwidth"
              >
                <thead>
                  <tr>
                    <th class="has-text-centered" />
                    <th class="has-text-centered">#</th>
                    <th>Εικόνα</th>
                    <th>Τίτλος (EL)</th>
                    <th>Τίτλος (EN)</th>
                    <th>Link</th>
                    <th class="has-text-centered">Εμφανές</th>
                    <th class="has-text-centered">Ημ/νια Δημ.</th>
                    <th class="has-text-centered">Ημ/νια Ενημ.</th>
                    <th class="hidden-print">Ενέργειες</th>
                  </tr>
                </thead>

                <draggable
                  v-model="items"
                  :move="canMove"
                  tag="tbody"
                  handle=".drag-handle"
                  @change="onReorder"
                >
                  <tr v-for="(item, idx) in items" :key="item.id">
                    <!-- Drag -->
                    <td class="has-text-centered">
                      <button
                        v-tooltip="'Σύρετε για αναδιάταξη (μόνο ενεργά)'"
                        :disabled="!item['is_active']"
                        class="button is-small is-white drag-handle"
                        type="button"
                      >
                        <span class="icon is-small">
                          <i class="fa fa-bars" />
                        </span>
                      </button>
                    </td>

                    <!-- Index -->
                    <td class="has-text-centered">{{ idx + 1 }}</td>

                    <!-- Thumbnail -->
                    <td style="width:80px">
                      <figure v-if="item['image_url']" class="image">
                        <img :src="item['image_url']" alt="thumb" />
                      </figure>
                      <span v-else>—</span>
                    </td>

                    <!-- Titles -->
                    <td>
                      <router-link
                        :to="{
                          name: 'content.home.standalone-cards.edit',
                          params: { id: item.id },
                        }"
                      >
                        {{ item.title_el }}
                      </router-link>
                    </td>
                    <td>{{ item.title_en }}</td>

                    <!-- Link -->
                    <td class="link-cell">
                      <a :href="item.link" target="_blank" rel="noopener">
                        {{ item.link }}
                      </a>
                    </td>

                    <!-- Active toggle -->
                    <td class="has-text-centered">
                      <ToggleButton
                        :value="!!item['is_active']"
                        :sync="true"
                        :color="{ checked: '#22A684', unchecked: '#ff3860' }"
                        :width="30"
                        :height="15"
                        @change="toggleActive(item, $event)"
                      />
                      <p class="help is-size-7 mt-1">Μέχρι 5 ενεργές κάρτες</p>
                    </td>

                    <!-- Dates -->
                    <td class="has-text-centered">
                      {{ formatDateTime(item.created_at) }}
                    </td>
                    <td class="has-text-centered">
                      {{ formatDateTime(item.updated_at) }}
                    </td>

                    <!-- Actions -->
                    <td class="hidden-print">
                      <div class="buttons are-small">
                        <router-link
                          :to="{
                            name: 'content.home.standalone-cards.edit',
                            params: { id: item.id },
                          }"
                          class="button is-small"
                          title="Επεξεργασία"
                        >
                          <span class="icon is-small">
                            <i class="fa fa-pencil" />
                          </span>
                        </router-link>

                        <button
                          class="button is-small is-outlined"
                          type="button"
                          @click="askToDelete(item)"
                        >
                          <span class="icon is-small">
                            <i class="fa fa-trash" />
                          </span>
                        </button>
                      </div>
                    </td>
                  </tr>
                </draggable>
              </table>

              <h4 v-else class="title is-4 has-text-centered">
                Δε βρέθηκαν στοιχεία
              </h4>
            </div>
          </div>
        </div>
      </div>

      <!-- Delete confirmation -->
      <Confirmation
        :model-name="selected ? selected.title_el : ''"
        :is-open="isConfirmOpen"
        description="Είστε βέβαιος για τη διαγραφή;"
        @closeModal="onConfirmClose"
      />
    </div>
  </LoadingContainer>
</template>

<script>
import draggable from 'vuedraggable';
import request from '@/utils/request';
import LoadingContainer from '@/views/components/LoadingContainer';
import Confirmation from '@/views/components/Confirmation';

export default {
  name: 'StandaloneCardLinksList',
  components: { draggable, LoadingContainer, Confirmation },
  data: () => ({
    isLoading: false,
    items: [],
    originalSnapshot: [],
    isConfirmOpen: false,
    selected: null,
  }),
  metaInfo: { title: 'Stand-alone Card Links' },

  created() {
    this.fetch();
  },

  methods: {
    async fetch() {
      try {
        this.isLoading = true;
        const { data } = await request.get('/home/standalone-card-links');

        // Normalize BE shape without nested ternaries / optional chaining
        let list = [];
        if (Array.isArray(data)) {
          list = data;
        } else if (data && Array.isArray(data.data)) {
          list = data.data;
        }

        this.items = list;
        this.snapshot();
      } catch (e) {
        this.$notify({
          type: 'error',
          title: 'Αποτυχία',
          text: 'To αίτημα απέτυχε',
        });
      } finally {
        this.isLoading = false;
      }
    },

    snapshot() {
      this.originalSnapshot = JSON.parse(JSON.stringify(this.items));
    },

    canMove(evt) {
      const di = evt?.draggedContext?.index;
      const ri = evt?.relatedContext?.index;
      if (typeof di !== 'number' || typeof ri !== 'number') return false;
      const dragged = this.items[di];
      const related = this.items[ri];
      // Only allow dragging from active to active targets
      // eslint-disable-next-line camelcase
      return !!dragged?.is_active && !!related?.is_active;
    },

    async onReorder() {
      // Send FULL mapping (all items), because BE requires contiguous orders 0..n-1
      const payload = this.items.map((it, idx) => ({ id: it.id, order: idx }));

      try {
        await request.patch('/home/standalone-card-links/reorder', {
          items: payload,
        });
        // reflect new order
        this.items.forEach((it, idx) => {
          this.$set(it, 'order', idx);
        });
        this.snapshot();
        this.$notify({
          type: 'success',
          title: 'Επιτυχία',
          text: 'Η αναδιάταξη ολοκληρώθηκε',
        });
      } catch (e) {
        // rollback UI
        this.items = JSON.parse(JSON.stringify(this.originalSnapshot));
        const msg =
          e?.response?.data?.message || e?.message || 'To αίτημα απέτυχε';
        this.$notify({ type: 'error', title: 'Αποτυχία', text: msg });
      }
    },

    async toggleActive(item, evt) {
      const next = evt?.value !== undefined ? !!evt.value : !item.is_active;
      const prev = !!item.is_active;

      this.$set(item, 'is_active', next);

      try {
        await request.patch(`/home/standalone-card-links/${item.id}`, {
          is_active: next,
        });

        if (next && (item.order === null || item.order === undefined)) {
          await this.fetch();
        }

        this.$notify({
          type: 'success',
          title: 'Επιτυχία',
          text: next
            ? 'Η κάρτα είναι τώρα εμφανής.'
            : 'Η κάρτα δεν είναι πλέον εμφανής.',
        });
      } catch (e) {
        // rollback on failure
        this.$set(item, 'is_active', prev);

        const msg =
          e?.response?.data?.message || e?.message || 'To αίτημα απέτυχε';
        this.$notify({
          type: 'error',
          title: 'Αποτυχία',
          text: msg.includes('Maximum of 5') ? 'Μέγιστο 5 ενεργές κάρτες' : msg,
        });
      }
    },

    askToDelete(item) {
      this.selected = item;
      this.isConfirmOpen = true;
    },

    async onConfirmClose(confirmed) {
      this.isConfirmOpen = false;
      if (!confirmed || !this.selected) return;

      try {
        const { data } = await request.delete(
          `/home/standalone-card-links/${this.selected.id}`,
        );
        if (data?.error) throw new Error(data.message || 'Server error');
        this.$notify({
          type: 'success',
          title: 'Επιτυχία',
          text: 'Η κάρτα διαγράφηκε',
        });
        await this.fetch();
      } catch (e) {
        this.$notify({
          type: 'error',
          title: 'Αποτυχία',
          text: 'To αίτημα απέτυχε',
        });
      } finally {
        this.selected = null;
      }
    },

    imageUrl(item) {
      // eslint-disable-next-line dot-notation
      return item && item['image_url'] ? item['image_url'] : '';
    },

    formatDateTime(v) {
      if (!v) return '-';
      const s = String(v).trim();
      const parts = s.replace('T', ' ').split(' ');
      if (parts.length) {
        const [y, m, d] = (parts[0] || '').split('-');
        const timePart = (parts[1] || '').slice(0, 5);
        if (y && m && d) {
          const pad = n => String(n).padStart(2, '0');
          return `${pad(d)}-${pad(m)}-${y} ${timePart || '00:00'}`;
        }
      }
      const ts = Date.parse(s);
      if (!Number.isNaN(ts)) {
        const dt = new Date(ts);
        const pad = n => String(n).padStart(2, '0');
        return `${pad(dt.getDate())}-${pad(
          dt.getMonth() + 1,
        )}-${dt.getFullYear()} ${pad(dt.getHours())}:${pad(dt.getMinutes())}`;
      }
      return s;
    },
  },
};
</script>

<style scoped>
.table-wrapper {
  position: relative;
}

.drag-handle {
  cursor: move;
}

.image img {
  object-fit: cover;
}

.link-cell {
  max-width: 420px;
  white-space: normal;
}

.link-cell a {
  display: inline-block;
  overflow-wrap: anywhere;
  word-break: break-word;
}
</style>
